import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Calendar, Compass, Crown, Star, Users, X, Sparkles, HelpCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { fetchPublicPlans } from '../services/api';
import { describePromo, formatCurrency, getHorizonLabel, getPlanFeatures, sortPlans } from '../utils/planCatalog';
import PayPalSubscriptionButton from './PayPalSubscriptionButton';

const ICON_MAP = {
  ESPEJO: Star,
  HORIZONTE_60: Compass,
  ORACULO_365: Crown
};

const GRADIENT_MAP = {
  ESPEJO: 'bg-blue-600',
  HORIZONTE_60: 'bg-pink-600',
  ORACULO_365: 'bg-amber-500'
};

const PricingPlans = ({ onSelectPlan }) => {
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlanCode, setSelectedPlanCode] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [billingCycle, setBillingCycle] = useState('annual'); // 'monthly' o 'annual'
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);

  // Mostrar banner de bienvenida si viene del registro
  useEffect(() => {
    if (location.state?.fromRegistration) {
      setShowWelcomeBanner(true);
      // Auto-ocultar después de 8 segundos
      const timer = setTimeout(() => setShowWelcomeBanner(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [location]);

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        setLoading(true);
        const data = await fetchPublicPlans();
        if (!isMounted) return;

        if (data && data.success && Array.isArray(data.plans)) {
          const sorted = sortPlans(data.plans);
          setPlans(sorted);
          setSelectedPlanCode(sorted[0]?.planCode || null);
          setError(null);
        } else {
          setPlans([]);
          setError(data?.error || 'No fue posible cargar el catálogo de planes.');
        }
      } catch (err) {
        if (!isMounted) return;
        setPlans([]);
        setError(err.message || 'No fue posible cargar el catálogo de planes.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const displayPlans = useMemo(() => plans.map((plan) => {
    const monthlyPrice = plan?.pricing?.prices?.monthly ?? null;
    const annualPrice = plan?.pricing?.prices?.annual ?? null;
    const promoMonthlyPrice = plan?.pricing?.prices?.promoMonthly ?? null;
    const promoAnnualPrice = plan?.pricing?.prices?.promoAnnual ?? null;
    const promo = plan?.pricing?.promo && plan.pricing.promo.isActive ? plan.pricing.promo : null;

    // ✅ Solo usar features del catálogo (sin agregar extras que dupliquen)
    let features = getPlanFeatures(plan.planCode);
    
    // 🎁 Agregar Planeador 2026 AL INICIO si es plan ANUAL (más visible)
    if (billingCycle === 'annual' && annualPrice) {
      features = [
        '📅 En planes anuales Incluye Planeador Numerológico 2026 (Se entrega en digital a partir del 10 de diciembre)',
        ...features
      ];
    }

    return {
      ...plan,
      monthlyPrice,
      annualPrice,
      promoMonthlyPrice,
      promoAnnualPrice,
      promo,
      features
    };
  }), [plans, billingCycle]);

  const selectedPlan = useMemo(
    () => displayPlans.find((plan) => plan.planCode === selectedPlanCode) || null,
    [displayPlans, selectedPlanCode]
  );

  const handleSelectPlan = (planCode) => {
    setSelectedPlanCode(planCode);
  };

  const handlePurchase = () => {
    if (!selectedPlan) return;

    // Verificar si el usuario está autenticado
    const token = localStorage.getItem('auth_token');
    if (!token) {
      // Guardar el plan seleccionado para después del login
      localStorage.setItem('selected_plan_code', selectedPlanCode);
      localStorage.setItem('selected_billing_cycle', billingCycle);
      
      // Redirigir a login con mensaje
      alert('Por favor inicia sesión para continuar con tu compra');
      window.location.href = '/login';
      return;
    }

    setShowCheckout(true);
  };

  // Calcular fechas de inicio y renovación
  const getSubscriptionDates = () => {
    const today = new Date();
    const startDate = new Date(today);
    const renewalDate = new Date(today);

    if (billingCycle === 'annual') {
      renewalDate.setFullYear(renewalDate.getFullYear() + 1);
    } else {
      renewalDate.setMonth(renewalDate.getMonth() + 1);
    }

    return {
      startDate: startDate.toLocaleDateString('es-MX', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      }),
      renewalDate: renewalDate.toLocaleDateString('es-MX', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    };
  };

  const handlePaymentSuccess = (subscriptionId) => {
    console.log('✅ Suscripción activada:', subscriptionId);
    setPaymentSuccess(true);
    setShowCheckout(false);
    
    // Notificar al componente padre si existe
    if (onSelectPlan) {
      onSelectPlan(selectedPlan.planCode);
    }

    // Mostrar mensaje de éxito por 3 segundos y luego recargar
    setTimeout(() => {
      window.location.href = '/app';
    }, 3000);
  };

  const handlePaymentError = (errorMessage) => {
    console.error('❌ Error en el pago:', errorMessage);
    alert(`Error al procesar el pago: ${errorMessage}\n\nPor favor, intenta de nuevo o contacta a soporte.`);
  };

  const handlePaymentCancel = () => {
    console.log('ℹ️ Usuario canceló el pago');
    setShowCheckout(false);
  };

  const closeCheckoutModal = () => {
    setShowCheckout(false);
  };

  // Calcular el precio según el ciclo de facturación
  const getSelectedPrice = () => {
    if (!selectedPlan) return 0;
    
    const hasPromo = selectedPlan.promo && selectedPlan.promo.isActive;
    
    if (billingCycle === 'annual') {
      // Si hay promo activa, usar precio promo anual
      if (hasPromo && selectedPlan.promoAnnualPrice) {
        return selectedPlan.promoAnnualPrice;
      }
      return selectedPlan.annualPrice || 0;
    } else {
      // Ciclo mensual: usar precio promo si existe, sino el normal
      if (hasPromo && selectedPlan.promoMonthlyPrice) {
        return selectedPlan.promoMonthlyPrice;
      }
      return selectedPlan.monthlyPrice || 0;
    }
  };

  const renderPlanCard = (plan, index) => {
    const Icon = ICON_MAP[plan.planCode] || Users;
    const gradient = GRADIENT_MAP[plan.planCode] || 'bg-morado';
    const isSelected = selectedPlanCode === plan.planCode;
    const hasPromo = Boolean(plan.promo);
    const annualDisplayPrice = hasPromo && plan.promoAnnualPrice ? plan.promoAnnualPrice : plan.annualPrice;
    const annualOriginal = hasPromo && plan.promoAnnualPrice ? plan.annualPrice : null;
    const monthlyDisplayPrice = hasPromo && plan.promoMonthlyPrice ? plan.promoMonthlyPrice : plan.monthlyPrice;
    const monthlyOriginal = hasPromo && plan.promoMonthlyPrice ? plan.monthlyPrice : null;

    return (
      <motion.div
        key={plan.planCode}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`
          relative rounded-2xl border-2 p-8 cursor-pointer transition-all duration-300 overflow-visible
          ${isSelected
            ? 'border-morado bg-white shadow-2xl transform scale-105 z-10'
            : 'border-gray-200 bg-white/80 shadow-lg hover:border-morado/50 hover:scale-105 hover:z-10'}
        `}
        onClick={() => handleSelectPlan(plan.planCode)}
      >
        {hasPromo && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-morado text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
              Promo activa
            </span>
          </div>
        )}

        <div className={`w-16 h-16 rounded-full ${gradient} flex items-center justify-center text-white mb-6 mx-auto`}>
          <Icon className="w-8 h-8" />
        </div>

        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-morado mb-2">{plan.displayName}</h3>
          {plan.tagline && <p className="text-gray-600 mb-4">{plan.tagline}</p>}
          <div className="space-y-2">
            {/* Precio Mensual */}
            {monthlyDisplayPrice && (
              <div className="flex flex-col items-center gap-1">
                {monthlyOriginal && (
                  <div className="text-sm text-gray-400 line-through">
                    {formatCurrency(monthlyOriginal)} <span className="text-xs">/mes</span>
                  </div>
                )}
                <div className={monthlyOriginal ? "text-lg font-bold text-morado" : "text-sm text-gray-600"}>
                  {formatCurrency(monthlyDisplayPrice)} <span className="text-xs text-gray-500 font-normal">/mes</span>
                </div>
              </div>
            )}
            {/* Precio Anual */}
            {annualOriginal && (
              <div className="text-2xl text-gray-400 line-through">
                {formatCurrency(annualOriginal)} <span className="text-sm">/año</span>
              </div>
            )}
            {annualDisplayPrice && (
              <div className="text-5xl font-bold text-morado">
                {formatCurrency(annualDisplayPrice)} <span className="text-xl text-gray-600 font-normal">/año</span>
              </div>
            )}
            {hasPromo && (
              <div className="text-xs font-semibold text-morado">
                {describePromo(plan.promo)}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {plan.features.map((feature, idx) => {
            const hasNinjaMention = feature.includes('Perfil Adicional') || feature.includes('Perfiles Adicionales');
            return (
              <div key={idx} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 text-sm flex-1 flex items-center gap-2">
                  {feature}
                  {hasNinjaMention && (
                    <div className="relative inline-block group">
                      <HelpCircle className="w-4 h-4 text-morado cursor-help flex-shrink-0" />
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-[9999] w-64 pointer-events-none">
                        <div className="bg-morado text-white text-xs rounded-lg p-3 shadow-2xl border-2 border-white">
                          <p className="font-semibold mb-1">¿Qué es un Perfil Adicional (Ninja)?</p>
                          <p className="text-white/90">Es una función que te permite agregar y consultar la información numerológica de otra persona (como tu pareja, hijos, familiares o clientes) y puedas también ver la información de sus días y energías numerológicas.</p>
                          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-morado rotate-45"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-4 right-4 w-6 h-6 bg-morado rounded-full flex items-center justify-center"
          >
            <Check className="w-4 h-4 text-white" />
          </motion.div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-morado/5 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Banner de bienvenida para nuevos usuarios */}
        <AnimatePresence>
          {showWelcomeBanner && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="mb-8 bg-morado rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 md:p-8 flex items-center gap-4">
                <div className="flex-shrink-0 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white mb-1">
                    ¡Bienvenido a Numerathum! 🎉
                  </h2>
                  <p className="text-white/80 text-sm md:text-base">
                    Tu cuenta ha sido creada exitosamente. Ahora selecciona un plan para comenzar tu viaje numerológico.
                  </p>
                </div>
                <button
                  onClick={() => setShowWelcomeBanner(false)}
                  className="flex-shrink-0 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-morado mb-4"
          >
            Elige tu Plan Numerológico
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 mb-2"
          >
            Visualiza tu horizonte y el de tus seres queridos
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.2 }}
            className="w-24 h-1 bg-morado rounded-full mx-auto"
          />
        </div>

        {loading ? (
          <div className="flex justify-center">
            <div className="flex items-center gap-3 px-6 py-4 bg-white rounded-2xl shadow border border-[#d4af37]/30">
              <div className="w-6 h-6 border-4 border-morado border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-morado font-medium">Cargando planes...</span>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-3xl mx-auto text-center bg-red-50 border border-red-200 rounded-2xl px-6 py-5 text-red-700">
            {error}
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-8 mb-12 overflow-visible">
              {displayPlans.map(renderPlanCard)}
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <button
                onClick={handlePurchase}
                disabled={!selectedPlan}
                className="bg-morado text-white px-12 py-4 rounded-xl text-lg font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedPlan
                  ? `Continuar con ${selectedPlan.displayName}`
                  : 'Selecciona un plan'}
              </button>
              <p className="text-gray-500 text-sm mt-4">
                🔒 Pagos seguros • Cancela cuando quieras • Renovación automática según periodo
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-16 text-center"
            >
              <div className="flex justify-center items-center gap-8 text-gray-400">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                    🔒
                  </div>
                  <span className="text-sm">Pago seguro</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                    💳
                  </div>
                  <span className="text-sm">PayPal</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                    🇲🇽
                  </div>
                  <span className="text-sm">Precios en MXN</span>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* Modal de Checkout con PayPal */}
        <AnimatePresence>
          {showCheckout && selectedPlan && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={closeCheckoutModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-morado px-6 py-4 rounded-t-2xl flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      Checkout
                    </h2>
                    <p className="text-white/80 text-sm">
                      {selectedPlan.displayName}
                    </p>
                  </div>
                  <button
                    onClick={closeCheckoutModal}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-6">
                  {/* Selector de ciclo de facturación */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Ciclo de Facturación:
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedPlan.monthlyPrice && (
                        <button
                          onClick={() => setBillingCycle('monthly')}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            billingCycle === 'monthly'
                              ? 'border-morado bg-morado/5'
                              : 'border-gray-200 hover:border-morado/30'
                          }`}
                        >
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">
                              Mensual
                            </div>
                            <div className="text-2xl font-bold text-morado mt-1">
                              {formatCurrency(
                                selectedPlan.promo && selectedPlan.promo.isActive && selectedPlan.promoMonthlyPrice
                                  ? selectedPlan.promoMonthlyPrice
                                  : selectedPlan.monthlyPrice
                              )}
                            </div>
                            {selectedPlan.promo && selectedPlan.promo.isActive && selectedPlan.promoMonthlyPrice && (
                              <div className="text-xs text-gray-400 line-through">
                                {formatCurrency(selectedPlan.monthlyPrice)}
                              </div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              por mes
                            </div>
                          </div>
                        </button>
                      )}
                      
                      {selectedPlan.annualPrice && (
                        <button
                          onClick={() => setBillingCycle('annual')}
                          className={`p-4 rounded-lg border-2 transition-all relative ${
                            billingCycle === 'annual'
                              ? 'border-morado bg-morado/5'
                              : 'border-gray-200 hover:border-morado/30'
                          }`}
                        >
                          {selectedPlan.promo && selectedPlan.promo.isActive && (
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                              ¡PROMO!
                            </div>
                          )}
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">
                              Anual
                            </div>
                            <div className="text-2xl font-bold text-morado mt-1">
                              {formatCurrency(
                                selectedPlan.promo && selectedPlan.promo.isActive && selectedPlan.promoAnnualPrice
                                  ? selectedPlan.promoAnnualPrice
                                  : selectedPlan.annualPrice
                              )}
                            </div>
                            {selectedPlan.promo && selectedPlan.promo.isActive && selectedPlan.promoAnnualPrice && (
                              <div className="text-xs text-gray-400 line-through">
                                {formatCurrency(selectedPlan.annualPrice)}
                              </div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              por año
                            </div>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Información de Suscripción */}
                  <div className="mb-6 bg-morado/5 rounded-lg p-4 border border-morado/20">
                    <h3 className="text-sm font-semibold text-morado mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Detalles de tu Suscripción
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Plan seleccionado:</span>
                        <span className="text-sm font-semibold text-morado">{selectedPlan.displayName}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Ciclo de facturación:</span>
                        <span className="text-sm font-semibold text-morado capitalize">{billingCycle === 'annual' ? 'Anual' : 'Mensual'}</span>
                      </div>
                      <div className="h-px bg-morado/20 my-2"></div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">📅 Fecha de inicio:</span>
                        <span className="text-sm font-semibold text-gray-900">{getSubscriptionDates().startDate}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">🔄 Próxima renovación:</span>
                        <span className="text-sm font-semibold text-gray-900">{getSubscriptionDates().renewalDate}</span>
                      </div>
                      <div className="h-px bg-morado/20 my-2"></div>
                      <div className="flex justify-between items-center">
                        <span className="text-base font-semibold text-gray-900">Total a pagar:</span>
                        <span className="text-2xl font-bold text-morado">{formatCurrency(getSelectedPrice())} MXN</span>
                      </div>
                    </div>
                  </div>

                  {/* Componente de PayPal Subscription */}
                  <PayPalSubscriptionButton
                    planId={selectedPlan.id}
                    planName={selectedPlan.display_name}
                    billingCycle={billingCycle}
                    amount={getSelectedPrice()}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    onCancel={handlePaymentCancel}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mensaje de éxito */}
        <AnimatePresence>
          {paymentSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  ¡Suscripción Activada!
                </h2>
                <p className="text-gray-600 mb-6">
                  Tu plan <span className="font-semibold">{selectedPlan?.displayName}</span> ha sido activado con renovación automática.
                </p>
                <div className="flex items-center justify-center gap-2 text-morado">
                  <div className="w-2 h-2 bg-morado rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-morado rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-morado rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Redirigiendo a tu dashboard...
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PricingPlans;
